import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import "./Modal.css";

// CLOSE_MS must equal the modalFadeOut duration defined in Modal.css.
// The component stays mounted for this duration so the CSS animation
// has time to fully play before React removes the node from the DOM.
const CLOSE_MS = 220;

// ─── Z-index hierarchy ────────────────────────────────────────────────────────
//
// The modal MUST render via createPortal(…, document.body).
//
// Why: .home-page has `animation: pageItemEnter … both` which keeps
// `transform: translateY(0)` applied via fill-mode after the animation settles.
// Any element with a non-none `transform` becomes the containing block for ALL
// position:fixed descendants AND creates an isolated stacking context.  That
// means .modal-overlay (position:fixed, z-index:1000) would be trapped inside
// .home-page's stacking context, unable to compete with elements outside it.
//
// The RtlsMap portal card lives directly in document.body (z-index 10 / 500).
// By also rendering the modal overlay directly in document.body we put both
// elements in the same root stacking context, where the hierarchy is simply:
//
//   RTLS map  (normal)   z-index: 10
//   RTLS map  (expanded) z-index: 500   ← Framer Motion mvZ.set(500)
//   Modal overlay        z-index: 1000  ← always wins
//
// ─────────────────────────────────────────────────────────────────────────────

const Modal = ({ isOpen, onClose, title, children, size = "large" }) => {
  // isClosing drives the --closing modifier class; the component stays
  // rendered while isClosing is true, then onClose() triggers unmount.
  const [isClosing, setIsClosing] = useState(false);

  // Single exit path - all three close triggers (button, backdrop, Escape)
  // call handleClose so the fade-out always fires before DOM removal.
  const handleClose = useCallback(() => {
    if (isClosing) return; // guard against double-trigger mid-animation
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false); // reset for next open cycle
      onClose(); // parent sets isOpen=false → component unmounts
    }, CLOSE_MS);
  }, [isClosing, onClose]);

  // Hold the body scroll lock for the full lifecycle including close animation.
  // Released only after the timer fires and the modal is truly gone.
  useEffect(() => {
    if (isOpen || isClosing) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen, isClosing]);

  // Escape key - route through handleClose for consistent animation behaviour.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") handleClose();
    };
    if (isOpen) document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, handleClose]);

  // Keep the node in the DOM while closing so the CSS animation renders.
  // Only return null when both flags are false (fully closed + not animating).
  if (!isOpen && !isClosing) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) handleClose();
  };

  // Portal to document.body - escapes the .home-page transform stacking
  // context so z-index: 1000 is compared in the root stacking context against
  // the RtlsMap portal card (z-index: 10 / 500).  Modal always wins.
  return createPortal(
    <div
      className={`modal-overlay${isClosing ? " modal-overlay--closing" : ""}`}
      onClick={handleBackdropClick}
    >
      <div className={`modal-container modal-${size}`}>
        <button
          className="modal-close"
          onClick={handleClose}
          aria-label="Close modal"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
        <div className="modal-content-body">
          {title && <h2 className="modal-title">{title}</h2>}
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default Modal;
